// MOC API client.
// Strategy: attempt the real Google Apps Script Web App first; if it is unreachable
// (CORS / network) or returns a non-matching payload, fall back to a local demo store
// so the preview stays fully explorable. The contract below is intentionally simple
// and centralised so it can be adjusted to the real API without touching UI code.
//
// EASY TO ADJUST: change `MOC_API_URL`, the `action` names, and `normalize*` mappers.

import {
  demoMocs, demoActions, demoValidations, demoNotifications,
  demoPssr, demoDashboard, documentsByMoc,
} from './demoData';
import {
  normDashboard, normMoc, normMocDetail, normAction, isRealAction, normNotification, normDocument,
} from './normalize';
import { SID_SECTIONS } from '../lib/sidChecklist';

export const MOC_API_URL = process.env.REACT_APP_MOC_API_URL;

// --- Live API helpers (direct browser -> Apps Script) ---
// GET uses query params; POST uses text/plain body to avoid CORS preflight.
// Apps Script response envelope: { success, data, error:{message,code}, timestamp }.
// We return `data` on success and throw on failure (which triggers the demo fallback).
async function readJson(res) {
  if (!res.ok) throw new Error(`API ${res.status}`);
  const text = await res.text();
  const t = text.trim();
  if (!t.startsWith('{') && !t.startsWith('[')) throw new Error('non-json (login/redirect?)');
  const json = JSON.parse(t);
  if (json && typeof json === 'object' && 'success' in json) {
    if (!json.success) throw new Error((json.error && json.error.code) || 'API_ERROR');
    return json.data;
  }
  return json;
}

// Calls go through our own backend proxy, which injects the Emergent-verified
// user email + shared secret so the Apps Script can authenticate the caller.
const PROXY = `${process.env.REACT_APP_BACKEND_URL}/api/moc`;

async function apiGet(action, params = {}) {
  const url = new URL(PROXY);
  url.searchParams.set('action', action);
  Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  const res = await fetch(url.toString(), { method: 'GET', credentials: 'include' });
  return readJson(res);
}

async function apiPost(action, payload = {}) {
  const url = `${PROXY}?action=${encodeURIComponent(action)}`;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return readJson(res);
}

const b64Size = (b64) => {
  const bytes = Math.round((b64.length * 3) / 4);
  return bytes > 1048576 ? `${(bytes / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(bytes / 1024))} Ko`;
};

// --- Local demo store (mutable, seeded from demo data) ---
const store = {
  mocs: JSON.parse(JSON.stringify(demoMocs)),
  actions: JSON.parse(JSON.stringify(demoActions)),
  validations: JSON.parse(JSON.stringify(demoValidations)),
  notifications: JSON.parse(JSON.stringify(demoNotifications)),
  pssr: JSON.parse(JSON.stringify(demoPssr)),
  dashboard: JSON.parse(JSON.stringify(demoDashboard)),
  documents: JSON.parse(JSON.stringify(documentsByMoc)),
};

export let LIVE_MODE = false; // flips true once any live call succeeds
const wrap = async (liveFn, fallbackFn) => {
  if (!MOC_API_URL) return fallbackFn();
  try {
    const data = await liveFn();
    LIVE_MODE = true;
    return data;
  } catch (e) {
    return fallbackFn();
  }
};

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

// ------- Public API used by the UI -------
export const mocApi = {
  async getDashboard() {
    return wrap(async () => normDashboard(await apiGet('getDashboardIndicators')), async () => { await delay(120); return store.dashboard; });
  },

  async getUserSites() {
    return wrap(() => apiGet('getUserSites'), async () => []);
  },
  async listSites() {
    try { const r = await apiGet('listSites'); return Array.isArray(r) && r.length ? r : null; } catch (e) { return null; }
  },
  async getCurrentUser() {
    try { return await apiGet('getCurrentUser'); } catch (e) { return null; }
  },

  async listMocs(filters = {}) {
    return wrap(async () => (await apiGet('listMocs', filters) || []).map(normMoc).filter((m) => m.id && !m.archived), async () => {
      await delay(120);
      let items = [...store.mocs];
      const { q, status, category, nature, site } = filters;
      if (q) {
        const s = q.toLowerCase();
        items = items.filter((m) => m.title.toLowerCase().includes(s) || m.id.toLowerCase().includes(s));
      }
      if (status) items = items.filter((m) => m.status === status);
      if (category) items = items.filter((m) => String(m.category) === String(category));
      if (nature) items = items.filter((m) => m.nature === nature);
      if (site) items = items.filter((m) => m.site === site);
      return items;
    });
  },

  async deleteMoc(mocId, mode) {
    try { await apiPost('deleteMoc', { mocId, mode }); return { ok: true }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },

  async getMoc(id) {
    return wrap(async () => {
      const [moc, docs, acts] = await Promise.all([
        apiGet('getMoc', { mocId: id, id }),
        apiGet('listMocDocuments', { mocId: id }).catch(() => []),
        apiGet('listActions', { mocId: id }).catch(() => []),
      ]);
      const detail = normMocDetail(moc);
      detail.documents = (docs || []).map(normDocument).filter((d) => d.id || d.name);
      detail.actions = (acts || []).map(normAction).filter(isRealAction)
        .filter((a) => !a.mocId || a.mocId === (detail.id || id));
      return detail;
    }, async () => {
      await delay(100);
      const moc = store.mocs.find((m) => m.id === id);
      const actions = store.actions.filter((a) => a.mocId === id);
      const documents = store.documents[id] || [];
      const pssr = store.pssr.find((p) => p.mocId === id) || null;
      return { ...moc, actions, documents, pssr };
    });
  },

  // Creates a draft MOC (Statut "Brouillon"). The guided assistant + submission happen afterwards.
  async createMoc(payload) {
    const mapped = {
      Titre: payload.title,
      Site_ID: payload.Site_ID || payload.site,
      Description_Changement: payload.futureSituation || payload.title,
      Type_Modification: payload.typeId || payload.Type_Modification || 'TYPE-05',
      Raison_Changement: payload.raison || 'Autre',
      Nature_Changement: payload.nature,
      Situation_Actuelle: payload.currentSituation,
      Situation_Souhaitee: payload.futureSituation,
      Service: payload.department,
    };
    if (payload.nature === 'Temporaire' && payload.temporaryEnd) mapped.Duree_Temporaire_Fin = payload.temporaryEnd;
    if (MOC_API_URL) {
      try {
        const data = await apiPost('createMoc', mapped);
        const m = normMoc(data);
        return { ok: true, id: m.id, moc: m };
      } catch (e) {
        return { ok: false, error: String((e && e.message) || e) };
      }
    }
    await delay(200);
    const id = `MOC-2026-${String(Math.floor(Math.random() * 900) + 100)}`;
    const moc = {
      id, title: payload.title || 'Nouvelle MOC', site: payload.site || 'Cimenterie Bouskoura',
      requester: payload.requester, category: 3, nature: payload.nature || 'Permanent',
      status: 'brouillon', alert: payload.nature === 'Urgent' ? 'critical' : 'info',
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
      department: payload.department || 'Procédé', pssrRequired: false,
      currentSituation: payload.currentSituation, futureSituation: payload.futureSituation,
      modificationTypes: payload.typeId ? [payload.typeId] : [], openActions: 0, lateActions: 0, ageDays: 0,
    };
    store.mocs.unshift(moc);
    return { ok: true, id, moc };
  },

  // Guided assistant (real referential MOC_Criteres via getAssistantQuestions).
  async getAssistantQuestions() {
    return wrap(async () => (await apiGet('getAssistantQuestions')) || [], async () => []);
  },
  async saveAssistantResponse(mocId, questionId, answer) {
    if (!MOC_API_URL) return { ok: true };
    try { await apiPost('saveAssistantResponse', { mocId, questionId, answer }); return { ok: true }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async evaluateAssistant(mocId) {
    if (!MOC_API_URL) return null;
    try { return await apiPost('evaluateAssistant', { mocId }); }
    catch (e) { return null; }
  },
  async submitMoc(mocId) {
    if (!MOC_API_URL) {
      const moc = store.mocs.find((m) => m.id === mocId);
      if (moc) { moc.status = 'soumise'; moc.updatedAt = new Date().toISOString(); }
      return { ok: true };
    }
    try { await apiPost('submitMoc', { mocId }); return { ok: true }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },

  async mocAction(id, action) {
    const A = {
      submit: 'submitMoc',
      start_risk: 'startRiskAnalysis', start_impl: 'markImplementationStarted',
      finish_impl: 'markImplementationComplete', ready_close: 'markReadyToClose',
      cancel: 'cancelMoc', close: 'closeMoc',
    }[action] || action;
    return wrap(() => apiPost(A, { mocId: id, id }), async () => {
      await delay(150);
      const moc = store.mocs.find((m) => m.id === id);
      if (!moc) return { ok: false };
      const map = {
        start_risk: 'analyse_risques', start_impl: 'implementation', finish_impl: 'pret_cloture',
        ready_close: 'pret_cloture', close: 'cloturee', cancel: 'annulee',
      };
      if (map[action]) { moc.status = map[action]; moc.updatedAt = new Date().toISOString(); }
      return { ok: true, moc };
    });
  },

  async listActions(mocId) {
    return wrap(async () => (await apiGet('listActions', mocId ? { mocId } : {}) || []).map(normAction).filter(isRealAction), async () => { await delay(100); return store.actions; });
  },
  async createAction(payload) {
    try { const d = await apiPost('createAction', payload); return { ok: true, data: normAction(d) }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async closeAction(actionId, evidence) {
    try { await apiPost('closeAction', { actionId, evidence }); return { ok: true }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async listApprobateurs(mocId) {
    try { return (await apiGet('listApprobateurs', { mocId })) || []; } catch (e) { return []; }
  },
  async listApprobations(mocId) {
    try { return (await apiGet('listApprobations', { mocId })) || []; } catch (e) { return []; }
  },
  async getUserRoles() {
    try { return (await apiGet('getUserRoles')) || []; } catch (e) { return []; }
  },
  async listUsers() {
    try { return (await apiGet('listUsers')) || []; } catch (e) { return []; }
  },
  async listRoles() {
    try { const r = await apiGet('listRoles'); return Array.isArray(r) && r.length ? r : null; } catch (e) { return null; }
  },
  async createUser(payload) {
    try { const d = await apiPost('createUser', payload); return { ok: true, data: d }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async updateUser(payload) {
    try { await apiPost('updateUser', payload); return { ok: true }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async setUserRoles(userId, roles) {
    try { await apiPost('setUserRoles', { Utilisateur_ID: userId, roles }); return { ok: true }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async listAllSites() {
    try { const r = await apiGet('listAllSites'); return Array.isArray(r) ? r : []; } catch (e) { return []; }
  },
  async createSite(payload) {
    try { const d = await apiPost('createSite', payload); return { ok: true, data: d }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async updateSite(payload) {
    try { await apiPost('updateSite', payload); return { ok: true }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async getSidConfig() {
    try { const r = await apiGet('getSidConfig'); return (r && Array.isArray(r.enabled_sections)) ? r : null; } catch (e) { return null; }
  },
  async getSidTemplate() {
    const cfg = await this.getSidConfig();
    const allIds = SID_SECTIONS.map((s) => s.id);
    let enabled = (cfg && Array.isArray(cfg.enabled_sections)) ? cfg.enabled_sections : null;
    if (!enabled) enabled = SID_SECTIONS.filter((s) => s.group !== 'equipement').map((s) => s.id);
    enabled = enabled.filter((id) => allIds.includes(id));
    return {
      sections: SID_SECTIONS.filter((s) => enabled.includes(s.id)),
      all_sections: SID_SECTIONS.map((s) => ({ id: s.id, title: s.title, count: s.items.length, group: s.group || 'general' })),
      enabled_sections: enabled,
    };
  },
  async updateSidConfig(enabledSections) {
    try { const d = await apiPost('setSidConfig', { enabled_sections: enabledSections }); return { ok: true, data: d }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async getSidResponses(mocId) {
    try { const r = await apiGet('getSidResponses', { mocId }); return { responses: (r && r.responses) || {} }; } catch (e) { return { responses: {} }; }
  },
  async saveSidResponses(mocId, responses) {
    try { const d = await apiPost('saveSidResponses', { mocId, responses }); return { ok: true, data: d }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async getSidCounts() {
    try { const r = await apiGet('getSidCounts'); return (r && typeof r === 'object' && !Array.isArray(r)) ? r : {}; } catch (e) { return {}; }
  },
  async parsePssrDocx(file) {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pssr/import/parse`, { method: 'POST', credentials: 'include', body: fd });
      if (!r.ok) { let m = 'Lecture impossible'; try { const j = await r.json(); m = j.detail || m; } catch (e2) { /* ignore */ } throw new Error(m); }
      return { ok: true, data: await r.json() };
    } catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },

  async getReminderSettings() {
    try { const r = await apiGet('getReminderSettings'); return r || null; } catch (e) { return null; }
  },
  async updateReminderSettings(payload) {
    try { const d = await apiPost('setReminderSettings', payload); return { ok: true, data: d }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },

  async listValidations() {
    return wrap(async () => (await apiGet('listMyPendingApprovals') || []).map((x) => {
      const m = normMoc(x);
      return {
        id: m.id, title: m.title, role: x.Role_Requis || 'Approbateur',
        requestedAt: x.Date_Demande || m.updatedAt, category: m.category,
        urgence: x.Urgence || 'Normale', synthese: x.Synthese || '', status: 'approbation',
      };
    }), async () => { await delay(100); return store.validations; });
  },

  // --- Classification (Pilote / Coordinateur MOC) ---
  async confirmClassification(mocId, decision, justification) {
    try { const d = await apiPost('confirmClassification', { mocId, decision, justification: justification || '' }); return { ok: true, data: d }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  // --- Approval workflow ---
  async requestApproval(mocId, approver) {
    try { const d = await apiPost('requestApproval', { mocId, approver }); return { ok: true, data: d }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async approveMoc(mocId, comment) {
    try { const d = await apiPost('approveMoc', { mocId, decision: 'Approuve', comment: comment || '' }); return { ok: true, data: d }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async rejectMoc(mocId, comment) {
    try { await apiPost('rejectMoc', { mocId, comment }); return { ok: true }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },

  async listNotifications() {
    return wrap(async () => (await apiGet('getUserNotifications') || []).map(normNotification), async () => { await delay(100); return store.notifications; });
  },

  async markNotificationRead(id) {
    return wrap(() => apiPost('markNotificationRead', { notificationId: id, id }), async () => {
      const n = store.notifications.find((x) => x.id === id);
      if (n) n.read = true;
      return { ok: true };
    });
  },

  async listPssr() {
    if (LIVE_MODE) return [];
    return wrap(() => apiGet('listPssr'), async () => { await delay(100); return store.pssr; });
  },

  // --- PSSR (Pre-Startup Safety Review). getPssr is keyed by mocId → {pssr, membres}. ---
  async createPssr(mocId) {
    try { const d = await apiPost('createPssr', { mocId }); return { ok: true, data: d }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async getPssr(mocId) {
    return wrap(() => apiGet('getPssr', { mocId }), async () => {
      await delay(80); const p = store.pssr.find((x) => x.mocId === mocId) || null;
      return p ? { pssr: p, membres: [] } : null;
    });
  },
  async getPssrChecklist(pssrId) {
    try { return (await apiGet('getPssrChecklist', { pssrId })) || []; } catch (e) { return []; }
  },
  async addPssrMembre(pssrId, utilisateurId, fonction) {
    try { const d = await apiPost('addPssrMembre', { PSSR_ID: pssrId, Utilisateur_ID: utilisateurId, Fonction: fonction }); return { ok: true, data: d }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async savePssrResponse(pssrId, questionId, reponse) {
    try { await apiPost('savePssrResponse', { PSSR_ID: pssrId, Question_ID: questionId, Reponse: reponse }); return { ok: true }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async signPssrMembre(membrePssrId, avis) {
    try { await apiPost('signPssrMembre', { membrePssrId, avis }); return { ok: true }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async authorizeStartup(mocId, decision) {
    try { await apiPost('authorizeStartup', { mocId, decision }); return { ok: true }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },

  // --- Closure ---
  async getClotureChecklist(mocId) {
    try { return (await apiGet('getClotureChecklist', { mocId })) || []; } catch (e) { return []; }
  },
  async saveClotureReponse(mocId, itemId, reponse, justification) {
    try { await apiPost('saveClotureReponse', { mocId, itemId, reponse, justification: justification || '' }); return { ok: true }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },
  async closeMoc(mocId) {
    if (!MOC_API_URL) {
      const moc = store.mocs.find((m) => m.id === mocId);
      if (moc) { moc.status = 'cloturee'; moc.updatedAt = new Date().toISOString(); }
      return { ok: true };
    }
    try { await apiPost('closeMoc', { mocId }); return { ok: true }; }
    catch (e) { return { ok: false, error: String((e && e.message) || e) }; }
  },

  // Documents — upload sends base64 to the API which stores in Drive.
  // The client NEVER receives or exposes a raw Drive ID; only an opaque content URL.
  async uploadDocument(mocId, { name, category, mime, dataBase64 }) {
    return wrap(
      () => apiPost('uploadMocDocument', { mocId, name, category, mime, content: dataBase64 }),
      async () => {
        await delay(300);
        const list = store.documents[mocId] || (store.documents[mocId] = []);
        // Versioning: archive the current live version with the same name.
        const prev = list.find((d) => d.name === name && !d.archived);
        if (prev) prev.archived = true;
        const doc = {
          id: `D${Date.now()}`, name, category,
          type: mime.includes('pdf') ? 'pdf' : 'image',
          size: b64Size(dataBase64), version: prev ? prev.version + 1 : 1,
          archived: false, at: new Date().toISOString(),
          _preview: `data:${mime};base64,${dataBase64}`,
        };
        list.unshift(doc);
        return { ok: true, document: doc };
      },
    );
  },

  async getDocumentContent(mocId, docId) {
    return wrap(() => apiGet('getDocumentContent', { documentId: docId, docId, mocId }), async () => {
      const d = (store.documents[mocId] || []).find((x) => x.id === docId);
      return { url: d?._preview || null, mime: d?.type };
    });
  },

  async archiveDocument(mocId, docId) {
    return wrap(() => apiPost('archiveDocument', { documentId: docId, docId, mocId }), async () => {
      const d = (store.documents[mocId] || []).find((x) => x.id === docId);
      if (d) d.archived = true;
      return { ok: true };
    });
  },
};

// ------- Local drafts (the ONLY business data kept client-side) -------
const DRAFT_KEY = 'moc_drafts';
export const draftStore = {
  all() { try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || '[]'); } catch { return []; } },
  save(draft) {
    const list = draftStore.all();
    const idx = list.findIndex((d) => d.localId === draft.localId);
    const item = { ...draft, updatedAt: new Date().toISOString() };
    if (idx >= 0) list[idx] = item; else list.push(item);
    localStorage.setItem(DRAFT_KEY, JSON.stringify(list));
    return item;
  },
  remove(localId) {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draftStore.all().filter((d) => d.localId !== localId)));
  },
};
